/**
 * PDF Extraction Module
 *
 * Client-side PDF text extraction using PDF.js.
 * Extracts text content from text-based PDFs.
 * Does NOT include PDF metadata (title, author, outlines).
 */

import * as pdfjsLib from 'pdfjs-dist';
import type { TextItem as PdfjsTextItem } from 'pdfjs-dist/types/src/display/api';

// Configure worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

/**
 * Text item from PDF.js with position info
 */
export interface TextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

/**
 * Page extraction result
 */
export interface PageResult {
  pageNum: number;
  items: TextItem[];
  text: string;
}

/**
 * Full extraction result
 */
export interface ExtractionResult {
  pages: PageResult[];
  fullText: string;
  totalChars: number;
  avgCharsPerPage: number;
}

/**
 * Extract text from a PDF file
 *
 * @param file - PDF file to extract from
 * @returns Extraction result with pages and full text
 */
export async function extractPdfText(file: File): Promise<ExtractionResult> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const pages: PageResult[] = [];
  let totalChars = 0;

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    const items: TextItem[] = textContent.items
      .filter((item): item is PdfjsTextItem => 'str' in item)
      .map((item) => ({
        str: item.str,
        transform: item.transform,
        width: item.width,
        height: item.height,
      }));

    // Join items into page text
    const pageText = itemsToText(items);
    totalChars += pageText.length;

    pages.push({
      pageNum,
      items,
      text: pageText,
    });
  }

  const fullText = pages.map((p) => p.text).join('\n\n');
  const avgCharsPerPage = pdf.numPages > 0 ? totalChars / pdf.numPages : 0;

  return {
    pages,
    fullText,
    totalChars,
    avgCharsPerPage,
  };
}

/**
 * Convert text items to string, grouping by lines based on y-position
 */
function itemsToText(items: TextItem[]): string {
  if (items.length === 0) return '';

  // Group items by approximate y-position (line)
  const lineThreshold = 5; // pixels tolerance for same line
  const lines: { y: number; items: TextItem[] }[] = [];

  for (const item of items) {
    // Y position is in transform[5]
    const y = item.transform[5];

    // Find existing line within threshold
    let foundLine = lines.find((line) => Math.abs(line.y - y) < lineThreshold);

    if (foundLine) {
      foundLine.items.push(item);
    } else {
      lines.push({ y, items: [item] });
    }
  }

  // Sort lines by y-position (descending - PDF y starts from bottom)
  lines.sort((a, b) => b.y - a.y);

  // Sort items within each line by x-position
  for (const line of lines) {
    line.items.sort((a, b) => a.transform[4] - b.transform[4]);
  }

  // Join items into text
  return lines
    .map((line) => line.items.map((item) => item.str).join(' '))
    .join('\n');
}
